import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'

import type {
  AnyTransition,
  DisplayGroupNode,
  StateMachineData,
  StateMachineEdge,
  StateMachineMetadataProps,
  StateMachineNode,
  StateMachineState
} from '../ProgramStateMachine.types'

import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { safeJsonParse } from '@percent/admin-dashboard/app/grants/programs/grantsProgramDetails/programStateMachine/utils/jsonUtils'
import { calculateLayout } from '@percent/admin-dashboard/app/grants/programs/grantsProgramDetails/programStateMachine/utils/layout'
import { useDefinitionHistory } from './useDefinitionHistory'

export interface StateMachineSaveErrorDetails {
  message?: string
  reasons?: string[]
}

interface UseStateMachineEditorProps {
  stateMachineData: StateMachineData | null
  programId: string
  metadata: StateMachineMetadataProps
  onSuccess?: () => void
  onError?: (details?: StateMachineSaveErrorDetails) => void
}

const extractSaveErrorDetails = (err: unknown): StateMachineSaveErrorDetails => {
  if (!isAxiosError<{ error?: { message?: string; reasons?: string[] } }>(err)) return {}

  const apiError = err.response?.data?.error

  return {
    message: apiError?.message,
    reasons: apiError?.reasons
  }
}

export interface UseStateMachineEditorReturn {
  nodes: StateMachineNode[]
  edges: StateMachineEdge[]
  groupNodes: DisplayGroupNode[]
  originalJson: string
  editedJson: string
  setEditedJson: (json: string) => void
  handleUpdate: () => Promise<void>
  handleSave: () => Promise<void>
  handleUndo: () => void
  handleRedo: () => void
  isUpdating: boolean
  localDefinition: StateMachineData | null
  addState: (state: StateMachineState) => void
  updateState: (state: StateMachineState) => void
  deleteState: (stateId: string) => void
  addTransition: (stateId: string, transition: AnyTransition) => void
  updateTransition: (stateId: string, transitionIndex: number, transition: AnyTransition) => void
  deleteTransition: (stateId: string, transitionIndex: number) => void
  reorderTransitions: (stateId: string, fromIndex: number, toIndex: number) => void
  updateGroupName: (oldName: string, newName: string) => void
  deleteGroup: (groupName: string) => void
  updateGroupMembers: (groupName: string, memberIds: string[], displayMode?: string, tableType?: string) => void
  canUndo: boolean
  canRedo: boolean
  hasUnsavedChanges: boolean
  metadata: StateMachineMetadataProps
}

export function useStateMachineEditor({
  stateMachineData,
  programId,
  metadata,
  onSuccess,
  onError
}: UseStateMachineEditorProps): UseStateMachineEditorReturn {
  const { t } = useTranslation()
  const { grantProgramService } = useServices()
  const [localDefinition, setLocalDefinition] = useState<StateMachineData | null>(null)
  const [nodes, setNodes] = useState<StateMachineNode[]>([])
  const [edges, setEdges] = useState<StateMachineEdge[]>([])
  const [groupNodes, setGroupNodes] = useState<DisplayGroupNode[]>([])
  const [editedJson, setEditedJson] = useState<string>('')
  const { pushSnapshot, undo, redo, canUndo, canRedo } = useDefinitionHistory()

  const originalJson = useMemo(
    () => (stateMachineData ? JSON.stringify(stateMachineData, null, 2) : '{\n}\n'),
    [stateMachineData]
  )

  const [{ isPending: isUpdating }, { mutateAsync: updateProgram }] = useMutation(
    grantProgramService.updateProgram,
    () => {
      onSuccess?.()
    },
    err => {
      onError?.(extractSaveErrorDetails(err))
    }
  )

  const hasUnsavedChanges = useMemo(() => {
    if (!localDefinition || !stateMachineData) return false

    return JSON.stringify(localDefinition) !== JSON.stringify(stateMachineData)
  }, [localDefinition, stateMachineData])

  // Initialize localDefinition from prop
  const initializedRef = useRef(false)
  useEffect(() => {
    if (stateMachineData) {
      const clone = JSON.parse(JSON.stringify(stateMachineData)) as StateMachineData
      setLocalDefinition(clone)

      if (!initializedRef.current) {
        pushSnapshot(clone)
        initializedRef.current = true
      }
    }
  }, [stateMachineData, pushSnapshot])

  // Helper to update localDefinition and push to history
  const applyDefinition = useCallback(
    (newDef: StateMachineData) => {
      setLocalDefinition(newDef)
      pushSnapshot(newDef)
    },
    [pushSnapshot]
  )

  // Recompute layout when localDefinition changes
  useEffect(() => {
    if (localDefinition?.states) {
      try {
        const { nodes: newNodes, edges: newEdges, groupNodes: newGroupNodes } = calculateLayout(localDefinition.states)
        setNodes(newNodes)
        setEdges(newEdges)
        setGroupNodes(newGroupNodes)
        setEditedJson(JSON.stringify(localDefinition, null, 2))
      } catch {
        onError?.()
      }
    } else {
      setNodes([])
      setEdges([])
      setGroupNodes([])
      setEditedJson(originalJson)
    }
  }, [localDefinition, onError, originalJson])

  // State mutations
  const addState = useCallback(
    (state: StateMachineState) => {
      if (!localDefinition) return
      applyDefinition({
        ...localDefinition,
        states: [...localDefinition.states, state]
      })
    },
    [localDefinition, applyDefinition]
  )

  const updateState = useCallback(
    (state: StateMachineState) => {
      if (!localDefinition) return
      applyDefinition({
        ...localDefinition,
        states: localDefinition.states.map(s => (s.id === state.id ? state : s))
      })
    },
    [localDefinition, applyDefinition]
  )

  const deleteState = useCallback(
    (stateId: string) => {
      if (!localDefinition) return
      applyDefinition({
        ...localDefinition,
        states: localDefinition.states
          .filter(s => s.id !== stateId)
          .map(s => ({
            ...s,
            transitions: s.transitions.filter(tr => tr.to !== stateId)
          }))
      })
    },
    [localDefinition, applyDefinition]
  )

  // Transition mutations
  const addTransition = useCallback(
    (stateId: string, transition: AnyTransition) => {
      if (!localDefinition) return
      applyDefinition({
        ...localDefinition,
        states: localDefinition.states.map(s =>
          s.id === stateId ? { ...s, transitions: [...s.transitions, transition] } : s
        )
      })
    },
    [localDefinition, applyDefinition]
  )

  const updateTransition = useCallback(
    (stateId: string, transitionIndex: number, transition: AnyTransition) => {
      if (!localDefinition) return
      applyDefinition({
        ...localDefinition,
        states: localDefinition.states.map(s => {
          if (s.id !== stateId) return s
          const newTransitions = [...s.transitions]
          newTransitions[transitionIndex] = transition

          return { ...s, transitions: newTransitions }
        })
      })
    },
    [localDefinition, applyDefinition]
  )

  const deleteTransition = useCallback(
    (stateId: string, transitionIndex: number) => {
      if (!localDefinition) return
      applyDefinition({
        ...localDefinition,
        states: localDefinition.states.map(s => {
          if (s.id !== stateId) return s

          return { ...s, transitions: s.transitions.filter((_, i) => i !== transitionIndex) }
        })
      })
    },
    [localDefinition, applyDefinition]
  )

  const reorderTransitions = useCallback(
    (stateId: string, fromIndex: number, toIndex: number) => {
      if (!localDefinition) return
      applyDefinition({
        ...localDefinition,
        states: localDefinition.states.map(s => {
          if (s.id !== stateId) return s
          const newTransitions = [...s.transitions]
          const [moved] = newTransitions.splice(fromIndex, 1)
          newTransitions.splice(toIndex, 0, moved)

          return { ...s, transitions: newTransitions }
        })
      })
    },
    [localDefinition, applyDefinition]
  )

  // Group mutations
  const updateGroupName = useCallback(
    (oldName: string, newName: string) => {
      if (!localDefinition) return
      applyDefinition({
        ...localDefinition,
        states: localDefinition.states.map(s =>
          s.metadata?.displayGroup === oldName ? { ...s, metadata: { ...s.metadata!, displayGroup: newName } } : s
        )
      })
    },
    [localDefinition, applyDefinition]
  )

  const deleteGroup = useCallback(
    (groupName: string) => {
      if (!localDefinition) return
      applyDefinition({
        ...localDefinition,
        states: localDefinition.states.map(s =>
          s.metadata?.displayGroup === groupName ? { ...s, metadata: { ...s.metadata!, displayGroup: undefined } } : s
        )
      })
    },
    [localDefinition, applyDefinition]
  )

  const updateGroupMembers = useCallback(
    (groupName: string, memberIds: string[], displayMode?: string, tableType?: string) => {
      if (!localDefinition) return
      const memberSet = new Set(memberIds)
      applyDefinition({
        ...localDefinition,
        states: localDefinition.states.map(s => {
          if (memberSet.has(s.id)) {
            // Add to group (and optionally update displayMode and tableType)
            return {
              ...s,
              metadata: {
                ...s.metadata!,
                displayGroup: groupName,
                ...(displayMode !== undefined ? { displayMode } : {}),
                ...(tableType !== undefined ? { tableType: tableType || null } : {})
              }
            }
          }

          if (s.metadata?.displayGroup === groupName) {
            // Remove from group (was previously in this group but not in new memberIds)
            return { ...s, metadata: { ...s.metadata, displayGroup: undefined } }
          }

          return s
        })
      })
    },
    [localDefinition, applyDefinition]
  )

  // Undo/Redo
  const handleUndo = useCallback(() => {
    const previous = undo()

    if (previous) {
      setLocalDefinition(previous)
    }
  }, [undo])

  const handleRedo = useCallback(() => {
    const next = redo()

    if (next) {
      setLocalDefinition(next)
    }
  }, [redo])

  // Save from canvas (uses localDefinition directly)
  const handleSave = useCallback(async () => {
    if (!localDefinition) return

    try {
      await updateProgram({
        programId,
        payload: { stateMachineDefinition: JSON.stringify(localDefinition) }
      })
    } catch {
      /* */
    }
  }, [localDefinition, programId, updateProgram])

  const handleUpdate = useCallback(async (): Promise<void> => {
    const payload = safeJsonParse(editedJson)

    if (!payload) {
      onError?.({ message: t('stateMachine.jsonEditor.invalidJson') })

      return
    }
    try {
      await updateProgram({
        programId,
        payload: { stateMachineDefinition: JSON.stringify(payload) }
      })
    } catch {
      /* */
    }
  }, [editedJson, onError, programId, t, updateProgram])

  return {
    nodes,
    edges,
    groupNodes,
    originalJson,
    editedJson,
    setEditedJson,
    handleUpdate,
    handleSave,
    handleUndo,
    handleRedo,
    isUpdating,
    localDefinition,
    addState,
    updateState,
    deleteState,
    addTransition,
    updateTransition,
    deleteTransition,
    reorderTransitions,
    updateGroupName,
    deleteGroup,
    updateGroupMembers,
    canUndo,
    canRedo,
    hasUnsavedChanges,
    metadata
  }
}
